import { useState, useRef, useCallback, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import ReactFlow, {
  useNodesState,
  useEdgesState,
  addEdge,
  Background,
  Controls,
  MiniMap,
  MarkerType,
  ReactFlowProvider,
} from "reactflow";
import "reactflow/dist/style.css";
import axios from "axios";
import { toast } from "sonner";
import dagre from "dagre";
import { useAuth } from "@/contexts/AuthContext";
import { recalculateAll } from "@/utils/calculations";
import { generateSummary } from "@/utils/summaryGenerator";
import { exportSpecsPdfWithDiagram, exportDiagramPng, exportDiagramPdf } from "@/utils/exportUtils";
import PlanningTable from "@/components/diagram/PlanningTable";
import Toolbox from "@/components/diagram/Toolbox";
import PropertiesPanel from "@/components/diagram/PropertiesPanel";
import {
  CompanyNode,
  SubsidiaryNode,
  JobNode,
  ScenarioNode,
  PersonNode,
  LogoNode,
  ServiceNode,
  NoteNode,
  MetricNode,
  HeaderBlock,
  HeroBlock,
  CtaBlock,
  SectionBlock,
  FooterBlock,
  FormBlock,
  InfoBlock,
  PriceBlock,
  LabelNode,
  LegendNode,
} from "@/components/diagram/CustomNodes";
import { ArrowLeft, Save, Share2, Presentation, X, ZoomIn, ZoomOut, Maximize2, Download, Undo2, Redo2, LayoutGrid, Search, Upload, Keyboard, FileJson, BookmarkPlus, Plus, MoreHorizontal, FileText, Loader2, Zap, Link, Minus, Mic, FilePlus, Eye, Circle, Key, CheckCircle, XCircle, Crown, History } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import AnimatedEdge from "@/components/diagram/AnimatedEdge";
import { EDGE_COLORS } from "@/components/diagram/AnimatedEdge";
import { OnboardingTour } from "@/components/diagram/OnboardingTour";
import { PresentationOverlay } from "@/components/diagram/PresentationOverlay";
import { AIChatPanel } from "@/components/diagram/AIChatPanel";
import { EnterpriseGateModal } from "@/components/diagram/EnterpriseGateModal";
import { useCollaboration } from "@/components/diagram/useCollaboration";
import { CollaborationCursors } from "@/components/diagram/CollaborationCursors";
import { CollaborationBar } from "@/components/diagram/CollaborationBar";
import { NotificationBell } from "@/components/NotificationBell";
import { VersionHistoryPanel } from "@/components/diagram/VersionHistory";

import { API_URL } from "@/utils/apiConfig";
const API = API_URL;

const nodeTypes = {
  company: CompanyNode,
  subsidiary: SubsidiaryNode,
  job: JobNode,
  scenario: ScenarioNode,
  person: PersonNode,
  logo: LogoNode,
  service: ServiceNode,
  note: NoteNode,
  metric: MetricNode,
  header: HeaderBlock,
  hero: HeroBlock,
  cta: CtaBlock,
  section: SectionBlock,
  footer: FooterBlock,
  form: FormBlock,
  info: InfoBlock,
  price: PriceBlock,
  label: LabelNode,
  legend: LegendNode,
};

const edgeTypes = {
  animated: AnimatedEdge,
};

const defaultEdgeOptions = {
  type: "animated",
  markerEnd: { type: MarkerType.ArrowClosed, color: "#6366F1", width: 16, height: 16 },
};


// Parse AI planning response into table rows
function parseAiPlanning(text) {
  const rows = [];
  const lines = text.split("\n");
  for (const line of lines) {
    if (!line.startsWith("|")) continue;
    const cells = line.split("|").map((c) => c.trim()).filter(Boolean);
    if (cells.length < 4) continue;
    if (cells.every((c) => /^[-:]+$/.test(c))) continue;
    if (cells[0].toLowerCase().includes("phase")) continue;
    const phase = (cells[0] || "MVP").replace(/\**/g, "").trim();
    if (!["MVP", "V1", "V2", "V3"].includes(phase)) continue;
    const jours = parseInt((cells[4] || "1").replace(/[^0-9]/g, "")) || 1;
    const priorite = (cells[6] || "P1").replace(/\**/g, "").trim();
    rows.push({
      id: Date.now().toString(36) + Math.random().toString(36).slice(2, 6),
      phase,
      composant: (cells[1] || "").replace(/\**/g, "").trim(),
      tache: (cells[2] || "").replace(/\**/g, "").trim(),
      responsable: (cells[3] || "Fullstack").replace(/\**/g, "").trim(),
      jours,
      priorite: ["P0", "P1", "P2"].includes(priorite) ? priorite : "P1",
      statut: "A faire",
    });
  }
  if (rows.length === 0) {
    // Fallback: generate basic rows from node data
    return [{ id: "fallback-1", phase: "MVP", composant: "Setup initial", tache: "Configuration projet, CI/CD, BDD", responsable: "DevOps", jours: 3, priorite: "P0", statut: "A faire" }];
  }
  return rows;
}

function getDefaultData(type) {
  const defaults = {
    company: {
      title: "Entreprise",
      amount: "",
      description: "",
      tags: [],
      links: [],
      customFields: [],
      color: "#6366F1",
    },
    subsidiary: {
      title: "Filiale",
      amount: "",
      description: "",
      tags: [],
      links: [],
      customFields: [],
      color: "#3B82F6",
    },
    job: {
      title: "Metier",
      amount: "",
      description: "",
      tags: [],
      links: [],
      customFields: [],
      color: "#F59E0B",
    },
    scenario: {
      title: "Condition",
      amount: "",
      description: "",
      tags: [],
      links: [],
      customFields: [],
      color: "#F43F5E",
      condition: "",
    },
    person: {
      title: "Nom Prenom",
      amount: "",
      description: "Role / Poste",
      tags: [],
      links: [],
      customFields: [],
      color: "#7C3AED",
    },
    logo: {
      title: "Logo",
      amount: "",
      description: "",
      tags: [],
      links: [],
      customFields: [],
      color: "#6366F1",
    },
    service: {
      title: "Service",
      amount: "",
      description: "Description du service",
      tags: [],
      links: [],
      customFields: [],
      color: "#0EA5E9",
    },
    note: {
      title: "",
      amount: "",
      description: "Ecrivez votre note ici...",
      tags: [],
      links: [],
      customFields: [],
      color: "#F59E0B",
    },
    metric: {
      title: "KPI",
      amount: "0",
      description: "",
      tags: [],
      links: [],
      customFields: [],
      color: "#10B981",
    },
    info: {
      title: "Bloc d'informations",
      description: "",
      tags: [],
      links: [],
      customFields: [],
      color: "#6366F1",
      listItems: ["Premier élément", "Deuxième élément"],
      fontSize: 13,
      nodeWidth: 288,
    },
    price: {
      title: "Prestation",
      description: "Description de la prestation",
      tags: [],
      links: [],
      customFields: [],
      color: "#10B981",
      priceHT: "1000",
      tvaRate: 20,
      fontSize: 13,
      nodeWidth: 240,
    },
    label: {
      title: "Titre",
      description: "",
      tags: [],
      links: [],
      customFields: [],
      color: "#1F2937",
      fontSize: 18,
    },
    legend: {
      title: "Legende",
      description: "",
      tags: [],
      links: [],
      customFields: [],
      color: "#8B5CF6",
      nodeWidth: 200,
      legendItems: [
        { color: "#6366F1", label: "Entreprise" },
        { color: "#3B82F6", label: "Filiale" },
        { color: "#F59E0B", label: "Metier" },
        { color: "#F43F5E", label: "Condition" },
      ],
    },
  };
  return defaults[type] || defaults.company;
}

function DiagramEditorInner() {
  const { diagramId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [diagramTitle, setDiagramTitle] = useState("");
  const [diagramType, setDiagramType] = useState("orgchart");
  const [isOwner, setIsOwner] = useState(true);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareEmail, setShareEmail] = useState("");
  const [shareLink, setShareLink] = useState("");
  const reactFlowWrapper = useRef(null);
  const containerRef = useRef(null);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const [saving, setSaving] = useState(false);
  const [presenting, setPresenting] = useState(false);
  const [presSlides, setPresSlides] = useState([]);
  const [presSlideIdx, setPresSlideIdx] = useState(0);
  const [editingEdgeId, setEditingEdgeId] = useState(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState(null);
  const [edgeLabel, setEdgeLabel] = useState("");
  const [edgeColor, setEdgeColor] = useState("indigo");
  const [edgeDirection, setEdgeDirection] = useState("forward");
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [templateDesc, setTemplateDesc] = useState("");
  const [templateCategory, setTemplateCategory] = useState("custom");
  const [mobileAddOpen, setMobileAddOpen] = useState(false);
  const [mobileMoreOpen, setMobileMoreOpen] = useState(false);
  const [mobileConnectMode, setMobileConnectMode] = useState(false);
  const [connectSource, setConnectSource] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [summaryTab, setSummaryTab] = useState("specs");
  const [planningRows, setPlanningRows] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const importRef = useRef(null);
  const { user } = useAuth();

  // Multi-pages
  const [pages, setPages] = useState([]);
  const [activePageId, setActivePageId] = useState(null);
  const [showArchView, setShowArchView] = useState(false);
  const [crossLinks, setCrossLinks] = useState([]); // [{id, sourcePageId, targetPageId, label}]
  const [mainCanvasBackup, setMainCanvasBackup] = useState({ nodes: [], edges: [] });

  // TTS Voice-off
  const [ttsEnabled, setTtsEnabled] = useState(false);
  const [ttsVoice, setTtsVoice] = useState("nova"); // nova=feminine, onyx=masculine
  const [ttsLoading, setTtsLoading] = useState(false);
  const ttsAudioRef = useRef(null);

  // Video recording
  const [isRecordingVideo, setIsRecordingVideo] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [videoBlobUrl, setVideoBlobUrl] = useState(null);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const videoRecorderRef = useRef(null);
  const videoChunksRef = useRef([]);
  const videoStreamRef = useRef(null);
  const recTimerRef = useRef(null);

  // Auto-play présentation
  const [autoPlaying, setAutoPlaying] = useState(false);
  const autoPlayTimerRef = useRef(null);

  // AI Assistant
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [assistantLoading, setAssistantLoading] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const chatEndRef = useRef(null);

  // Accèsses panel
  const [accessesOpen, setAccessesOpen] = useState(false);
  const [accesses, setAccesses] = useState([]);
  const [newAccessLabel, setNewAccessLabel] = useState("");
  const [newAccessType, setNewAccessType] = useState("api_key");
  const [newAccessFor, setNewAccessFor] = useState("client");

  // Feature gating
  const [upgradePromptOpen, setUpgradePromptOpen] = useState(false);
  const [upgradeFeatureName, setUpgradeFeatureName] = useState("");
  const userPlan = user?.plan || "free";

  const requireEnterprise = useCallback((featureName) => {
    if (userPlan === "enterprise" || user?.role === "admin") return true;
    setUpgradeFeatureName(featureName);
    setUpgradePromptOpen(true);
    return false;
  }, [userPlan, user]);

  // Onboarding step
  const [onboardingStep, setOnboardingStep] = useState(-1);

  // Auto-start tour if ?tour=1 in URL
  useEffect(() => {
    if (searchParams.get("tour") === "1") {
      const timer = setTimeout(() => setOnboardingStep(0), 1500);
      return () => clearTimeout(timer);
    }
  }, [searchParams]);

  // ─── COLLABORATION ───
  const isEnterprise = userPlan === "enterprise" || user?.role === "admin";
  const collabRemoteRef = useRef(false); // flag to avoid echo-loops

  const handleCollabNodeMove = useCallback((nodeId, position) => {
    collabRemoteRef.current = true;
    setNodes(nds => nds.map(n => n.id === nodeId ? { ...n, position } : n));
    setTimeout(() => { collabRemoteRef.current = false; }, 50);
  }, [setNodes]);

  const handleCollabNodesUpdate = useCallback((newNodes, newEdges) => {
    collabRemoteRef.current = true;
    if (newNodes) setNodes(newNodes);
    if (newEdges) setEdges(newEdges);
    setTimeout(() => { collabRemoteRef.current = false; }, 50);
  }, [setNodes, setEdges]);

  const handleCollabNodeAdd = useCallback((node) => {
    collabRemoteRef.current = true;
    setNodes(nds => [...nds, node]);
    setTimeout(() => { collabRemoteRef.current = false; }, 50);
  }, [setNodes]);

  const handleCollabNodeRemove = useCallback((nodeId) => {
    collabRemoteRef.current = true;
    setNodes(nds => nds.filter(n => n.id !== nodeId));
    setEdges(eds => eds.filter(e => e.source !== nodeId && e.target !== nodeId));
    setTimeout(() => { collabRemoteRef.current = false; }, 50);
  }, [setNodes, setEdges]);

  const handleCollabEdgeAdd = useCallback((edge) => {
    collabRemoteRef.current = true;
    setEdges(eds => [...eds, edge]);
    setTimeout(() => { collabRemoteRef.current = false; }, 50);
  }, [setEdges]);

  const handleCollabEdgeRemove = useCallback((edgeId) => {
    collabRemoteRef.current = true;
    setEdges(eds => eds.filter(e => e.id !== edgeId));
    setTimeout(() => { collabRemoteRef.current = false; }, 50);
  }, [setEdges]);

  const handleCollabNodeDataUpdate = useCallback((nodeId, data) => {
    collabRemoteRef.current = true;
    setNodes(nds => nds.map(n => n.id === nodeId ? { ...n, data: { ...n.data, ...data } } : n));
    setTimeout(() => { collabRemoteRef.current = false; }, 50);
  }, [setNodes]);

  const collab = useCollaboration({
    diagramId,
    enabled: isEnterprise,
    onNodeMove: handleCollabNodeMove,
    onNodesUpdate: handleCollabNodesUpdate,
    onNodeAdd: handleCollabNodeAdd,
    onNodeRemove: handleCollabNodeRemove,
    onEdgeAdd: handleCollabEdgeAdd,
    onEdgeRemove: handleCollabEdgeRemove,
    onNodeDataUpdate: handleCollabNodeDataUpdate,
  });

  useEffect(() => {
    if (user && user.onboarding_done === false) {
      const timer = setTimeout(() => setOnboardingStep(0), 1500);
      return () => clearTimeout(timer);
    }
  }, [user]);

  // Undo/Redo
  const historyRef = useRef([]);
  const redoRef = useRef([]);
  const nodesRef = useRef([]);
  const edgesRef = useRef([]);
  useEffect(() => { nodesRef.current = nodes; }, [nodes]);
  useEffect(() => { edgesRef.current = edges; }, [edges]);

  const pushHistory = useCallback(() => {
    historyRef.current.push({
      nodes: JSON.parse(JSON.stringify(nodesRef.current)),
      edges: JSON.parse(JSON.stringify(edgesRef.current)),
    });
    redoRef.current = [];
    if (historyRef.current.length > 50) historyRef.current.shift();
  }, []);

  const undo = useCallback(() => {
    if (historyRef.current.length === 0) return;
    redoRef.current.push({
      nodes: JSON.parse(JSON.stringify(nodesRef.current)),
      edges: JSON.parse(JSON.stringify(edgesRef.current)),
    });
    const prev = historyRef.current.pop();
    setNodes(prev.nodes);
    setEdges(prev.edges);
    toast.success("Annule");
  }, [setNodes, setEdges]);

  const redo = useCallback(() => {
    if (redoRef.current.length === 0) return;
    historyRef.current.push({
      nodes: JSON.parse(JSON.stringify(nodesRef.current)),
      edges: JSON.parse(JSON.stringify(edgesRef.current)),
    });
    const next = redoRef.current.pop();
    setNodes(next.nodes);
    setEdges(next.edges);
    toast.success("Retabli");
  }, [setNodes, setEdges]);

  // Load diagram
  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/diagrams/${diagramId}`, {
          withCredentials: true,
        });
        setDiagramTitle(data.title);
        setDiagramType(data.diagram_type);
        setIsOwner(data.is_owner);
        if (data.nodes?.length > 0) setNodes(data.nodes);
        if (data.edges?.length > 0) setEdges(data.edges);
        if (data.pages?.length > 0) setPages(data.pages);
        if (data.cross_links?.length > 0) setCrossLinks(data.cross_links);
        // Load accesses
        try {
          const { data: accs } = await axios.get(`${API}/diagrams/${diagramId}/accesses`, { withCredentials: true });
          setAccesses(accs || []);
        } catch { /* ignore */ }
        // Trigger calculation after load
        setTimeout(() => {
          const loadedNodes = data.nodes || [];
          const loadedEdges = data.edges || [];
          const hasFormulas = loadedNodes.some((n) => n.data?.formula?.type);
          if (hasFormulas) {
            const recalculated = recalculateAll(loadedNodes, loadedEdges);
            setNodes(recalculated);
          }
        }, 300);
      } catch {
        toast.error("Erreur lors du chargement");
        navigate("/dashboard");
      }
    };
    if (diagramId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diagramId]);

  // Save
  const saveDiagram = useCallback(async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/diagrams/${diagramId}`,
        {
          title: diagramTitle,
          diagram_type: diagramType,
          nodes: nodes.map((n) => ({
            id: n.id,
            type: n.type,
            position: n.position,
            data: n.data,
          })),
          edges: edges.map((e) => ({
            id: e.id,
            source: e.source,
            target: e.target,
            sourceHandle: e.sourceHandle,
            targetHandle: e.targetHandle,
            type: e.type,
            label: e.label,
            style: e.style,
            data: e.data,
            markerEnd: e.markerEnd,
          })),
          pages,
          cross_links: crossLinks,
        },
        { withCredentials: true }
      );
      toast.success("Diagramme sauvegarde");
    } catch {
      toast.error("Erreur lors de la sauvegarde");
    }
    setSaving(false);
  }, [diagramId, diagramTitle, diagramType, nodes, edges, pages, crossLinks]);

  // Connect
  const onConnect = useCallback(
    (params) => {
      pushHistory();
      const newEdge = {
        ...params,
        id: `e-${Date.now()}`,
        type: "animated",
        markerEnd: { type: MarkerType.ArrowClosed, color: "#6366F1", width: 16, height: 16 },
      };
      setEdges((eds) => addEdge(newEdge, eds));
      if (!collabRemoteRef.current && collab.connected) {
        collab.sendEdgeAdd(newEdge);
      }
    },
    [setEdges, pushHistory, collab]
  );

  // Drag & drop
  const onDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  }, []);

  const onDrop = useCallback(
    (e) => {
      e.preventDefault();
      const type = e.dataTransfer.getData("application/reactflow");
      if (!type || !reactFlowInstance) return;
      const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
      const position = reactFlowInstance.project({
        x: e.clientX - reactFlowBounds.left,
        y: e.clientY - reactFlowBounds.top,
      });
      const newNode = {
        id: `${type}-${Date.now()}`,
        type,
        position,
        data: getDefaultData(type),
      };
      pushHistory();
      setNodes((prev) => [...prev, newNode]);
      if (collab.connected) {
        collab.sendNodeAdd(newNode);
      }
    },
    [reactFlowInstance, setNodes, pushHistory, collab]
  );

  // Node click
  const onNodeClick = useCallback((_, node) => {
    // Mobile connect mode: select source then target
    if (mobileConnectMode) {
      if (!connectSource) {
        setConnectSource(node.id);
        toast.info(`Source: ${node.data.title || node.id}. Touchez la cible.`);
      } else if (node.id !== connectSource) {
        pushHistory();
        setEdges((eds) =>
          addEdge(
            {
              id: `e-${connectSource}-${node.id}-${Date.now()}`,
              source: connectSource,
              target: node.id,
              type: "animated",
              markerEnd: { type: MarkerType.ArrowClosed, color: "#6366F1", width: 16, height: 16 },
            },
            eds
          )
        );
        toast.success("Connexion créée !");
        setConnectSource(null);
        setMobileConnectMode(false);
      }
      return;
    }
    setSelectedNode(node);
  }, [mobileConnectMode, connectSource, pushHistory, setEdges]);

  // Update node data
  const updateNodeData = useCallback(
    (nodeId, newData) => {
      setNodes((prev) =>
        prev.map((n) =>
          n.id === nodeId ? { ...n, data: { ...n.data, ...newData } } : n
        )
      );
      setSelectedNode((prev) =>
        prev && prev.id === nodeId
          ? { ...prev, data: { ...prev.data, ...newData } }
          : prev
      );
      if (!collabRemoteRef.current && collab.connected) {
        collab.sendNodeDataUpdate(nodeId, newData);
      }
    },
    [setNodes, collab]
  );

  // Delete node
  const deleteNode = useCallback(
    (nodeId) => {
      pushHistory();
      setNodes((prev) => prev.filter((n) => n.id !== nodeId));
      setEdges((prev) =>
        prev.filter((e) => e.source !== nodeId && e.target !== nodeId)
      );
      setSelectedNode(null);
      if (!collabRemoteRef.current && collab.connected) {
        collab.sendNodeRemove(nodeId);
      }
    },
    [setNodes, setEdges, pushHistory, collab]
  );

  const deleteEdge = useCallback(
    (edgeId) => {
      pushHistory();
      setEdges((prev) => prev.filter((e) => e.id !== edgeId));
      setSelectedEdgeId(null);
      if (!collabRemoteRef.current && collab.connected) {
        collab.sendEdgeRemove(edgeId);
      }
    },
    [setEdges, pushHistory, collab]
  );


  // Pane click
  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
    setSelectedEdgeId(null);
    if (mobileConnectMode) {
      setConnectSource(null);
      setMobileConnectMode(false);
      toast.info("Mode connexion annule");
    }
  }, [mobileConnectMode]);

  // Edge double-click → edit label
  const onEdgeDoubleClick = useCallback((_, edge) => {
    setEditingEdgeId(edge.id);
    setEdgeLabel(edge.label || "");
    setEdgeColor(edge.data?.color || "indigo");
    setEdgeDirection(edge.data?.direction || "forward");
  }, []);

  // Single click on edge: select it for deletion
  const onEdgeClick = useCallback((_, edge) => {
    setSelectedEdgeId(edge.id);
  }, []);

  const saveEdgeLabel = useCallback(() => {
    if (editingEdgeId) {
      pushHistory();
      setEdges((prev) =>
        prev.map((e) => {
          if (e.id !== editingEdgeId) return e;
          const colorPalette = EDGE_COLORS[edgeColor] || EDGE_COLORS.indigo;
          const arrowMarker = { type: MarkerType.ArrowClosed, color: colorPalette.accent, width: 16, height: 16 };
          let markerEnd = undefined;
          let markerStart = undefined;
          if (edgeDirection === "forward") {
            markerEnd = arrowMarker;
          } else if (edgeDirection === "backward") {
            markerStart = arrowMarker;
          } else if (edgeDirection === "both") {
            markerEnd = arrowMarker;
            markerStart = arrowMarker;
          }
          // "none" = no markers
          return {
            ...e,
            label: edgeLabel || undefined,
            data: { ...e.data, color: edgeColor, direction: edgeDirection },
            markerEnd,
            markerStart,
          };
        })
      );
      setEditingEdgeId(null);
      setEdgeLabel("");
      setEdgeColor("indigo");
      setEdgeDirection("forward");
    }
  }, [editingEdgeId, edgeLabel, edgeColor, edgeDirection, setEdges, pushHistory]);

  // Export PNG (with framing and signature)
  const exportPng = useCallback(async () => {
    try {
      await exportDiagramPng(diagramTitle);
      toast.success("Image exportee avec signature CardFlow");
    } catch {
      toast.error("Erreur lors de l'export");
    }
  }, [diagramTitle]);

  // Export PDF diagram
  const exportPdf = useCallback(async () => {
    try {
      await exportDiagramPdf(diagramTitle);
      toast.success("PDF du schéma exporte");
    } catch {
      toast.error("Erreur lors de l'export PDF");
    }
  }, [diagramTitle]);

  // Auto-layout with dagre
  const autoLayout = useCallback(() => {
    if (nodes.length === 0) return;
    pushHistory();
    const g = new dagre.graphlib.Graph();
    g.setDefaultEdgeLabel(() => ({}));
    g.setGraph({ rankdir: "TB", nodesep: 80, ranksep: 120, marginx: 40, marginy: 40 });
    nodes.forEach((n) => g.setNode(n.id, { width: 288, height: 180 }));
    edges.forEach((e) => g.setEdge(e.source, e.target));
    dagre.layout(g);
    const laid = nodes.map((n) => {
      const pos = g.node(n.id);
      return { ...n, position: { x: pos.x - 144, y: pos.y - 90 } };
    });
    setNodes(laid);
    setTimeout(() => {
      if (reactFlowInstance) reactFlowInstance.fitView({ padding: 0.12, duration: 400 });
    }, 50);
    toast.success("Layout applique");
  }, [nodes, edges, setNodes, pushHistory, reactFlowInstance]);

  // Duplicate selected node
  const duplicateNode = useCallback(() => {
    if (!selectedNode) return;
    pushHistory();
    const newNode = {
      id: `${selectedNode.type}-${Date.now()}`,
      type: selectedNode.type,
      position: {
        x: selectedNode.position.x + 320,
        y: selectedNode.position.y + 30,
      },
      data: JSON.parse(JSON.stringify(selectedNode.data)),
    };
    newNode.data.title = `${newNode.data.title} (copie)`;
    setNodes((prev) => [...prev, newNode]);
    toast.success("Élément duplique");
  }, [selectedNode, setNodes, pushHistory]);

  // Add node at viewport center (mobile touch)
  const addNodeAtCenter = useCallback((type) => {
    if (!reactFlowInstance) return;
    pushHistory();
    const { x, y, zoom } = reactFlowInstance.getViewport();
    const centerX = (-x + 195) / zoom;
    const centerY = (-y + 400) / zoom;
    const newNode = {
      id: `${type}-${Date.now()}`,
      type,
      position: { x: centerX - 100, y: centerY - 50 },
      data: getDefaultData(type),
    };
    setNodes((prev) => [...prev, newNode]);
    setMobileAddOpen(false);
    toast.success("Élément ajoute");
  }, [reactFlowInstance, setNodes, pushHistory]);

  // Export JSON
  const exportJson = useCallback(() => {
    const data = {
      title: diagramTitle,
      diagram_type: diagramType,
      nodes: nodes.map((n) => ({ id: n.id, type: n.type, position: n.position, data: n.data })),
      edges: edges.map((e) => ({ id: e.id, source: e.source, target: e.target, sourceHandle: e.sourceHandle, targetHandle: e.targetHandle, type: e.type, label: e.label, style: e.style, markerEnd: e.markerEnd })),
      exportedAt: new Date().toISOString(),
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.download = `${diagramTitle || "diagram"}.json`;
    link.href = url;
    link.click();
    URL.revokeObjectURL(url);
    toast.success("JSON exporte");
  }, [diagramTitle, diagramType, nodes, edges]);

  // Save as template
  const saveAsTemplate = useCallback(async () => {
    try {
      await saveDiagram(); // Save first
      await axios.post(
        `${API}/diagrams/${diagramId}/save-as-template`,
        {
          title: templateName || diagramTitle,
          description: templateDesc,
          category: templateCategory,
        },
        { withCredentials: true }
      );
      toast.success("Modele sauvegarde !");
      setSaveTemplateOpen(false);
      setTemplateName("");
      setTemplateDesc("");
    } catch {
      toast.error("Erreur lors de la sauvegarde");
    }
  }, [templateName, templateDesc, templateCategory, diagramTitle, diagramId, saveDiagram]);

  // Import JSON
  const handleImportJson = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = JSON.parse(ev.target.result);
        if (data.nodes && Array.isArray(data.nodes)) {
          pushHistory();
          setNodes(data.nodes);
          if (data.edges && Array.isArray(data.edges)) setEdges(data.edges);
          if (data.title) setDiagramTitle(data.title);
          if (data.diagram_type) setDiagramType(data.diagram_type);
          setTimeout(() => {
            if (reactFlowInstance) reactFlowInstance.fitView({ padding: 0.12, duration: 400 });
          }, 100);
          toast.success("Diagramme importe");
        } else {
          toast.error("Format JSON invalide");
        }
      } catch {
        toast.error("Erreur de lecture du fichier");
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  }, [pushHistory, setNodes, setEdges, reactFlowInstance]);

  // Dynamic calculations — recalculate when edges change or formula is set
  const runCalculations = useCallback(() => {
    setNodes((prev) => {
      const hasFormulas = prev.some((n) => n.data.formula?.type);
      if (!hasFormulas) return prev;
      const recalculated = recalculateAll(prev, edgesRef.current);
      // Only update if values changed
      const changed = recalculated.some(
        (n, i) => n.data.calculatedAmount !== prev[i]?.data?.calculatedAmount
      );
      return changed ? recalculated : prev;
    });
  }, [setNodes]);

  // Trigger recalculation when edges change
  const prevEdgeKeyRef = useRef("");
  useEffect(() => {
    const edgeKey = edges.map((e) => `${e.source}-${e.target}`).sort().join(",");
    if (edgeKey !== prevEdgeKeyRef.current) {
      prevEdgeKeyRef.current = edgeKey;
      runCalculations();
    }
  }, [edges, runCalculations]);

  // Search nodes
  useEffect(() => {
    if (!searchTerm.trim()) {
      setNodes((prev) => prev.map((n) => ({ ...n, style: undefined })));
      return;
    }
    const term = searchTerm.toLowerCase();
    setNodes((prev) =>
      prev.map((n) => {
        const d = n.data;
        const match =
          d.title?.toLowerCase().includes(term) ||
          d.description?.toLowerCase().includes(term) ||
          d.amount?.toLowerCase().includes(term) ||
          d.tags?.some((t) => t.toLowerCase().includes(term)) ||
          d.customFields?.some((f) => f.key.toLowerCase().includes(term) || f.value.toLowerCase().includes(term));
        return {
          ...n,
          style: match
            ? { boxShadow: "0 0 0 3px #7C3AED", zIndex: 10 }
            : { opacity: 0.15 },
        };
      })
    );
  }, [searchTerm, setNodes]);

  // Connect mode node highlighting
  useEffect(() => {
    if (!mobileConnectMode || searchTerm.trim()) return;
    setNodes((prev) =>
      prev.map((n) => ({
        ...n,
        style: n.id === connectSource
          ? { boxShadow: "0 0 0 3px #7C3AED", zIndex: 10 }
          : undefined,
      }))
    );
  }, [mobileConnectMode, connectSource, setNodes, searchTerm]);

  // Clear styles when exiting connect mode
  const connectModeInitRef = useRef(true);
  useEffect(() => {
    if (connectModeInitRef.current) {
      connectModeInitRef.current = false;
      return;
    }
    if (!mobileConnectMode && !searchTerm.trim()) {
      setNodes((prev) => {
        const needsClear = prev.some((n) => n.style !== undefined);
        if (!needsClear) return prev;
        return prev.map((n) => ({ ...n, style: undefined }));
      });
    }
  }, [mobileConnectMode, setNodes, searchTerm]);

  // Share
  const shareWithUser = async () => {
    try {
      await axios.post(
        `${API}/diagrams/${diagramId}/share`,
        { email: shareEmail },
        { withCredentials: true }
      );
      toast.success(`Partage avec ${shareEmail}`);
      setShareEmail("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const createShareLink = async () => {
    try {
      const { data } = await axios.post(
        `${API}/diagrams/${diagramId}/share-link`,
        {},
        { withCredentials: true }
      );
      const link = `${window.location.origin}/shared/${data.share_id}`;
      setShareLink(link);
      try { await navigator.clipboard.writeText(link); } catch { /* fallback: user copies manually */ }
      toast.success("Lien copié !");
    } catch {
      toast.error("Erreur");
    }
  };

  // (keyboard handler moved below slide functions)

  // ─── MULTI-PAGES ───
  // nodesRef and edgesRef already defined above

  const switchToPage = useCallback((pageId) => {
    const currentNodes = nodesRef.current.map(n => ({ id: n.id, type: n.type, position: n.position, data: n.data }));
    const currentEdges = edgesRef.current.map(e => ({ id: e.id, source: e.source, target: e.target, sourceHandle: e.sourceHandle, targetHandle: e.targetHandle, type: e.type, label: e.label, data: e.data, markerEnd: e.markerEnd }));

    if (activePageId) {
      setPages(prev => prev.map(p => p.id === activePageId ? { ...p, nodes: currentNodes, edges: currentEdges } : p));
    } else if (!showArchView) {
      setMainCanvasBackup({ nodes: currentNodes, edges: currentEdges });
    }

    setShowArchView(false);

    if (pageId === null) {
      setActivePageId(null);
      if (mainCanvasBackup.nodes.length > 0) {
        setNodes(mainCanvasBackup.nodes);
        setEdges(mainCanvasBackup.edges);
      }
      setTimeout(() => {
        if (reactFlowInstance) reactFlowInstance.fitView({ padding: 0.15, duration: 400 });
      }, 100);
      return;
    }

    setActivePageId(pageId);
    const page = pages.find(p => p.id === pageId);
    if (page) {
      setNodes(page.nodes || []);
      setEdges(page.edges || []);
      setSelectedNode(null);
      setTimeout(() => {
        if (reactFlowInstance) reactFlowInstance.fitView({ padding: 0.15, duration: 400 });
      }, 100);
    }
  }, [activePageId, pages, mainCanvasBackup, showArchView, setNodes, setEdges, reactFlowInstance]);

  const showArchitecture = useCallback(() => {
    // Save current state first
    const currentNodes = nodesRef.current.map(n => ({ id: n.id, type: n.type, position: n.position, data: n.data }));
    const currentEdges = edgesRef.current.map(e => ({ id: e.id, source: e.source, target: e.target, sourceHandle: e.sourceHandle, targetHandle: e.targetHandle, type: e.type, label: e.label, data: e.data, markerEnd: e.markerEnd }));
    if (activePageId) {
      setPages(prev => prev.map(p => p.id === activePageId ? { ...p, nodes: currentNodes, edges: currentEdges } : p));
    } else {
      setMainCanvasBackup({ nodes: currentNodes, edges: currentEdges });
    }

    // Build architecture view nodes: one block per page
    const archNodes = [];
    const archEdges = [];
    const spacing = 400;

    // Main canvas as first block
    archNodes.push({
      id: "arch-main",
      type: "company",
      position: { x: spacing, y: 0 },
      data: {
        title: diagramTitle || "Vue principale",
        description: `${mainCanvasBackup.nodes.length || currentNodes.length} éléments`,
        tags: ["principal"],
        color: "#6366F1",
        customFields: [],
        links: [],
        amount: "",
      },
    });

    // Pages as blocks
    pages.forEach((page, i) => {
      const cols = Math.min(pages.length + 1, 4);
      const row = Math.floor((i + 1) / cols);
      const col = (i + 1) % cols;
      archNodes.push({
        id: `arch-${page.id}`,
        type: "service",
        position: { x: col * spacing, y: (row + 1) * 280 },
        data: {
          title: page.title,
          description: `${(page.nodes || []).length} éléments`,
          tags: ["page"],
          color: ["#3B82F6", "#7C3AED", "#10B981", "#F59E0B", "#F43F5E", "#0EA5E9"][i % 6],
          customFields: [],
          links: [],
          amount: "",
        },
      });
      // Edge from main to each page
      archEdges.push({
        id: `arch-e-main-${page.id}`,
        source: "arch-main",
        target: `arch-${page.id}`,
        type: "animated",
        markerEnd: { type: "arrowclosed", color: "#6366F1" },
      });
    });

    // Cross-page links
    crossLinks.forEach((link) => {
      archEdges.push({
        id: `arch-cl-${link.id}`,
        source: link.sourcePageId === "main" ? "arch-main" : `arch-${link.sourcePageId}`,
        target: link.targetPageId === "main" ? "arch-main" : `arch-${link.targetPageId}`,
        type: "animated",
        label: link.label || "",
        data: { color: "rose" },
        markerEnd: { type: "arrowclosed", color: "#F43F5E" },
      });
    });

    setActivePageId(null);
    setShowArchView(true);
    setNodes(archNodes);
    setEdges(archEdges);
    setSelectedNode(null);
    setTimeout(() => {
      if (reactFlowInstance) reactFlowInstance.fitView({ padding: 0.2, duration: 400 });
    }, 100);
  }, [activePageId, pages, crossLinks, diagramTitle, mainCanvasBackup, setNodes, setEdges, reactFlowInstance]);

  const addCrossLink = useCallback((sourcePageId, targetPageId, label) => {
    setCrossLinks(prev => [...prev, {
      id: `cl-${Date.now()}`,
      sourcePageId,
      targetPageId,
      label: label || "",
    }]);
  }, []);

  const addPage = useCallback((title) => {
    const newPage = {
      id: `page-${Date.now()}`,
      title: title || `Page ${pages.length + 1}`,
      nodes: [],
      edges: [],
    };
    setPages(prev => [...prev, newPage]);
    toast.success(`Page "${newPage.title}" ajoutee`);
    return newPage;
  }, [pages]);

  const removePage = useCallback((pageId) => {
    if (activePageId === pageId) {
      setActivePageId(null);
    }
    setPages(prev => prev.filter(p => p.id !== pageId));
    setCrossLinks(prev => prev.filter(cl => cl.sourcePageId !== pageId && cl.targetPageId !== pageId));
    toast.success("Page supprimée");
  }, [activePageId]);

  // ─── AI ASSISTANT ───
  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: "audio/webm" });
      audioChunksRef.current = [];
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        if (blob.size < 1000) return;
        setAssistantLoading(true);
        try {
          const formData = new FormData();
          formData.append("file", blob, "recording.webm");
          const { data } = await axios.post(`${API}/ai/transcribe`, formData, {
            withCredentials: true,
            headers: { "Content-Type": "multipart/form-data" },
          });
          if (data.text) {
            setChatMessages(prev => [...prev, { role: "user", text: data.text, type: "voice" }]);
          }
        } catch {
          toast.error("Erreur de transcription");
        }
        setAssistantLoading(false);
      };
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setIsRecording(true);
    } catch {
      toast.error("Accès au micro refuse");
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  }, [isRecording]);

  const sendChatMessage = useCallback(() => {
    const text = chatInput.trim();
    if (!text) return;
    setChatMessages(prev => [...prev, { role: "user", text, type: "text" }]);
    setChatInput("");
  }, [chatInput]);

  const generateFromChat = useCallback(async () => {
    const userMessages = chatMessages.filter(m => m.role === "user").map(m => m.text);
    if (userMessages.length === 0) return;
    setAssistantLoading(true);
    setChatMessages(prev => [...prev, { role: "assistant", text: "Generation du schéma en cours...", type: "loading" }]);
    try {
      const fullTranscript = userMessages.join("\n\n");
      const { data } = await axios.post(`${API}/ai/assistant`, {
        transcript: fullTranscript,
        diagram_id: diagramId,
        context: diagramTitle,
      }, { withCredentials: true });

      // Remove the loading message
      setChatMessages(prev => {
        const filtered = prev.filter(m => m.type !== "loading");
        const pageNames = (data.pages || []).map(p => p.title).join(", ");
        return [...filtered, { role: "assistant", text: `Schema genere avec ${(data.pages || []).length} page(s) : ${pageNames || "Vue principale"}`, type: "result" }];
      });

      if (data.title) setDiagramTitle(data.title);
      if (data.pages && data.pages.length > 0) {
        const firstPage = data.pages[0];
        if (firstPage.nodes) setNodes(firstPage.nodes);
        if (firstPage.edges) setEdges(firstPage.edges);
        if (data.pages.length > 1) {
          setPages(data.pages.slice(1));
        }
        toast.success(`Schéma genere : ${data.pages.length} page(s)`);
      }
      setTimeout(() => {
        if (reactFlowInstance) reactFlowInstance.fitView({ padding: 0.12, duration: 400 });
      }, 200);
    } catch (e) {
      setChatMessages(prev => {
        const filtered = prev.filter(m => m.type !== "loading");
        return [...filtered, { role: "assistant", text: e.response?.data?.detail || "Erreur de generation. Reessayez.", type: "error" }];
      });
    }
    setAssistantLoading(false);
  }, [chatMessages, diagramId, diagramTitle, setNodes, setEdges, reactFlowInstance]);

  // Auto-scroll chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages]);

  // Presentation mode — auto-generate slides from diagram structure
  const buildSlides = useCallback(() => {
    if (nodes.length === 0) return [];
    const slides = [];
    // Slide 0: overview (fit all)
    slides.push({ type: "overview", label: "Vue d'ensemble", nodeIds: nodes.map(n => n.id) });

    // Find root nodes (no incoming edges)
    const targetIds = new Set(edges.map(e => e.target));
    const roots = nodes.filter(n => !targetIds.has(n.id));
    const visited = new Set();

    // BFS from roots to create step-by-step slides
    const queue = [...roots];
    if (queue.length === 0 && nodes.length > 0) queue.push(nodes[0]);

    while (queue.length > 0) {
      const node = queue.shift();
      if (visited.has(node.id)) continue;
      visited.add(node.id);

      // Find connected children
      const childEdges = edges.filter(e => e.source === node.id);
      const childNodes = childEdges.map(e => nodes.find(n => n.id === e.target)).filter(Boolean);
      const groupIds = [node.id, ...childNodes.map(c => c.id)];

      slides.push({
        type: "step",
        label: node.data.title || "Étape",
        nodeIds: groupIds,
        mainNodeId: node.id,
      });

      for (const child of childNodes) {
        if (!visited.has(child.id)) queue.push(child);
      }
    }

    // Add any unvisited nodes
    const unvisited = nodes.filter(n => !visited.has(n.id));
    if (unvisited.length > 0) {
      slides.push({ type: "other", label: "Autres elements", nodeIds: unvisited.map(n => n.id) });
    }

    return slides;
  }, [nodes, edges]);

  const enterPresentation = useCallback(() => {
    const slides = buildSlides();
    setPresSlides(slides);
    setPresSlideIdx(0);
    setPresenting(true);
    setSelectedNode(null);
    const el = containerRef.current;
    if (el?.requestFullscreen) {
      el.requestFullscreen().catch(() => {});
    } else if (el?.webkitRequestFullscreen) {
      el.webkitRequestFullscreen();
    }
    // Fit to first slide after a delay
    setTimeout(() => {
      if (reactFlowInstance && slides.length > 0) {
        const firstNodeIds = slides[0].nodeIds;
        const slideNodes = nodes.filter(n => firstNodeIds.includes(n.id));
        if (slideNodes.length > 0) {
          reactFlowInstance.fitView({ nodes: slideNodes, padding: 0.3, duration: 600 });
        } else {
          reactFlowInstance.fitView({ padding: 0.15, duration: 400 });
        }
      }
    }, 400);
  }, [reactFlowInstance, buildSlides, nodes]);

  const exitPresentation = useCallback(() => {
    setPresenting(false);
    setPresSlides([]);
    setPresSlideIdx(0);
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    } else if (document.webkitFullscreenElement) {
      document.webkitExitFullscreen();
    }
  }, []);

  const goToSlide = useCallback((idx) => {
    if (idx < 0 || idx >= presSlides.length || !reactFlowInstance) return;
    setPresSlideIdx(idx);
    const slide = presSlides[idx];
    const slideNodes = nodes.filter(n => slide.nodeIds.includes(n.id));
    if (slideNodes.length > 0) {
      reactFlowInstance.fitView({ nodes: slideNodes, padding: 0.35, duration: 600 });
    }
  }, [presSlides, nodes, reactFlowInstance]);

  const nextSlide = useCallback(() => {
    goToSlide(presSlideIdx + 1);
  }, [presSlideIdx, goToSlide]);

  const prevSlide = useCallback(() => {
    goToSlide(presSlideIdx - 1);
  }, [presSlideIdx, goToSlide]);

  // TTS narration for presentation — uses AI to interpret the slide
  const narrateSlide = useCallback(async (slideIndex) => {
    if (!ttsEnabled || !presSlides[slideIndex]) return;
    const slide = presSlides[slideIndex];
    const slideNodes = nodes.filter(n => slide.nodeIds.includes(n.id));
    const slideEdges = edges.filter(e => {
      const nodeIds = new Set(slide.nodeIds);
      return nodeIds.has(e.source) && nodeIds.has(e.target);
    });

    setTtsLoading(true);
    try {
      if (ttsAudioRef.current) {
        ttsAudioRef.current.pause();
        ttsAudioRef.current = null;
      }

      // Call AI to generate rich narration
      const { data: narrationData } = await axios.post(`${API}/ai/narrate-slide`, {
        diagram_title: diagramTitle,
        slide_label: slide.label,
        slide_index: slideIndex,
        total_slides: presSlides.length,
        nodes: slideNodes.map(n => ({ id: n.id, type: n.type, data: { title: n.data?.title, description: n.data?.description, amount: n.data?.amount, tags: n.data?.tags, contentItems: n.data?.contentItems } })),
        edges: slideEdges.map(e => ({ source: e.source, target: e.target })),
        is_intro: slideIndex === 0,
      }, { withCredentials: true });

      const narrationText = narrationData.narration;
      if (!narrationText || narrationText.length < 5) { setTtsLoading(false); return; }

      // Generate TTS audio from narration
      const { data: ttsData } = await axios.post(`${API}/ai/tts`, { text: narrationText, voice: ttsVoice }, { withCredentials: true });
      if (ttsData.url) {
        const audio = new Audio(`${API.replace("/api", "")}${ttsData.url}`);
        ttsAudioRef.current = audio;
        await new Promise((resolve) => {
          audio.onended = resolve;
          audio.onerror = resolve;
          audio.play().catch(resolve);
        });
      }
    } catch {
      // Fallback: simple narration without AI
      try {
        const fallbackText = slideIndex === 0
          ? `Présentation du projet ${diagramTitle}. ${slide.label}.`
          : `Étape ${slideIndex + 1}, ${slide.label}. ${slideNodes.map(n => n.data?.title).filter(Boolean).join(", ")}.`;
        const { data: ttsData } = await axios.post(`${API}/ai/tts`, { text: fallbackText, voice: ttsVoice }, { withCredentials: true });
        if (ttsData.url) {
          const audio = new Audio(`${API.replace("/api", "")}${ttsData.url}`);
          ttsAudioRef.current = audio;
          await new Promise((resolve) => {
            audio.onended = resolve;
            audio.onerror = resolve;
            audio.play().catch(resolve);
          });
        }
      } catch {
        // Silent fail
      }
    }
    setTtsLoading(false);
  }, [ttsEnabled, presSlides, nodes, edges, ttsVoice, diagramTitle]);

  // Auto-narrate on slide change
  useEffect(() => {
    if (presenting && ttsEnabled && !autoPlaying) {
      narrateSlide(presSlideIdx);
    }
    return () => {
      if (ttsAudioRef.current) { ttsAudioRef.current.pause(); ttsAudioRef.current = null; }
    };
  }, [presSlideIdx, presenting, ttsEnabled, narrateSlide, autoPlaying]);

  // Auto-play: advance through slides with TTS
  const runAutoPlay = useCallback(async () => {
    if (!presenting) return;
    setAutoPlaying(true);
    setTtsEnabled(true);

    for (let i = presSlideIdx; i < presSlides.length; i++) {
      goToSlide(i);
      await narrateSlide(i);
      // Wait a short pause between slides
      await new Promise(r => { autoPlayTimerRef.current = setTimeout(r, 1500); });
      // Check if auto-play was cancelled
      if (!autoPlayTimerRef.current) break;
    }
    setAutoPlaying(false);
  }, [presenting, presSlideIdx, presSlides, goToSlide, narrateSlide]);

  const toggleAutoPlay = useCallback(() => {
    if (autoPlaying) {
      // Stop auto-play
      setAutoPlaying(false);
      clearTimeout(autoPlayTimerRef.current);
      autoPlayTimerRef.current = null;
      if (ttsAudioRef.current) { ttsAudioRef.current.pause(); ttsAudioRef.current = null; }
    } else {
      runAutoPlay();
    }
  }, [autoPlaying, runAutoPlay]);

  // Create client review link
  const createReviewLink = useCallback(async () => {
    try {
      await saveDiagram();
      const { data } = await axios.post(`${API}/reviews/create`, { diagram_id: diagramId }, { withCredentials: true });
      const url = `${window.location.origin}/review/${data.review_id}`;
      await navigator.clipboard.writeText(url);
      toast.success("Lien de review copie !");
      return url;
    } catch {
      toast.error("Erreur creation du lien");
    }
  }, [diagramId, saveDiagram]);

  // Video recording of présentation
  const startVideoRecording = useCallback(async () => {
    try {
      const el = containerRef.current;
      if (!el) return;
      const canvas = el.querySelector("canvas") || el;
      let stream;
      if (canvas.captureStream) {
        stream = canvas.captureStream(30);
      } else {
        stream = await navigator.mediaDevices.getDisplayMedia({
          video: { mediaSource: "screen", width: 1920, height: 1080 },
          audio: true,
        });
      }
      videoStreamRef.current = stream;
      videoChunksRef.current = [];
      setVideoReady(false);
      setVideoBlobUrl(null);
      setRecordingDuration(0);
      const recorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported("video/webm;codecs=vp9") ? "video/webm;codecs=vp9" : "video/webm",
      });
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) videoChunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(videoChunksRef.current, { type: "video/webm" });
        const url = URL.createObjectURL(blob);
        setVideoBlobUrl(url);
        setVideoReady(true);
        clearInterval(recTimerRef.current);
        toast.success("Enregistrement terminé ! Cliquez sur VIDEO pour telecharger.");
        if (videoStreamRef.current) {
          videoStreamRef.current.getTracks().forEach(t => t.stop());
          videoStreamRef.current = null;
        }
      };
      videoRecorderRef.current = recorder;
      recorder.start(100);
      setIsRecordingVideo(true);
      // Start duration timer
      recTimerRef.current = setInterval(() => {
        setRecordingDuration(d => d + 1);
      }, 1000);
      toast.success("Enregistrement demarre — votre ecran est capture");
    } catch (e) {
      toast.error("Impossible de demarrer l'enregistrement");
      console.error(e);
    }
  }, []);

  const stopVideoRecording = useCallback(() => {
    if (videoRecorderRef.current && isRecordingVideo) {
      videoRecorderRef.current.stop();
      setIsRecordingVideo(false);
      clearInterval(recTimerRef.current);
    }
  }, [isRecordingVideo]);

  const downloadVideo = useCallback(() => {
    if (videoBlobUrl) {
      const a = document.createElement("a");
      a.href = videoBlobUrl;
      a.download = `${(diagramTitle || "présentation").replace(/\s+/g, "-").toLowerCase()}-presentation.webm`;
      a.click();
      toast.success("Video WebM telechargee !");
    }
  }, [videoBlobUrl, diagramTitle]);

  // MP4 conversion state
  const [mp4Converting, setMp4Converting] = useState(false);
  const [mp4Ready, setMp4Ready] = useState(false);
  const [mp4Url, setMp4Url] = useState(null);

  const convertToMp4 = useCallback(async () => {
    if (!videoBlobUrl) return;
    setMp4Converting(true);
    try {
      const resp = await fetch(videoBlobUrl);
      const blob = await resp.blob();
      const formData = new FormData();
      formData.append("file", blob, "presentation.webm");
      const { data } = await axios.post(`${API}/video/convert`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMp4Url(`${API.replace("/api", "")}${data.url}`);
      setMp4Ready(true);
      toast.success("Conversion MP4 terminée !");
    } catch {
      toast.error("Erreur de conversion MP4");
    }
    setMp4Converting(false);
  }, [videoBlobUrl]);

  // Stop recording when exiting présentation
  const exitPresentationWithCleanup = useCallback(() => {
    if (isRecordingVideo) stopVideoRecording();
    if (ttsAudioRef.current) { ttsAudioRef.current.pause(); ttsAudioRef.current = null; }
    setAutoPlaying(false);
    clearTimeout(autoPlayTimerRef.current);
    autoPlayTimerRef.current = null;
    exitPresentation();
  }, [isRecordingVideo, stopVideoRecording, exitPresentation]);

  // Ctrl+S + Escape + Undo/Redo + Présentation navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        saveDiagram();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === "z" && !e.shiftKey) {
        e.preventDefault();
        undo();
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === "y" || (e.key === "z" && e.shiftKey))) {
        e.preventDefault();
        redo();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === "d") {
        e.preventDefault();
        duplicateNode();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === "f") {
        e.preventDefault();
        setSearchOpen((v) => !v);
        if (searchOpen) { setSearchTerm(""); }
      }
      if (e.key === "?" && !e.ctrlKey && !e.metaKey && document.activeElement?.tagName !== "INPUT" && document.activeElement?.tagName !== "TEXTAREA") {
        setShortcutsOpen(true);
      }
      if (e.key === "Escape") {
        if (searchOpen) { setSearchOpen(false); setSearchTerm(""); }
        if (presenting) { exitPresentationWithCleanup(); }
      }
      if (presenting) {
        if (e.key === "ArrowRight" || e.key === " " || e.key === "ArrowDown") {
          e.preventDefault();
          nextSlide();
        }
        if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
          e.preventDefault();
          prevSlide();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [saveDiagram, presenting, undo, redo, duplicateNode, searchOpen, nextSlide, prevSlide, exitPresentationWithCleanup]);

  // Listen for fullscreen exit (e.g. browser Escape)
  useEffect(() => {
    const onFullscreenChange = () => {
      if (!document.fullscreenElement && !document.webkitFullscreenElement) {
        setPresenting(false);
      }
    };
    document.addEventListener("fullscreenchange", onFullscreenChange);
    document.addEventListener("webkitfullscreenchange", onFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", onFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", onFullscreenChange);
    };
  }, []);

  const presentationZoomIn = useCallback(() => {
    if (reactFlowInstance) reactFlowInstance.zoomIn({ duration: 200 });
  }, [reactFlowInstance]);

  const presentationZoomOut = useCallback(() => {
    if (reactFlowInstance) reactFlowInstance.zoomOut({ duration: 200 });
  }, [reactFlowInstance]);

  const presentationFitView = useCallback(() => {
    if (reactFlowInstance) reactFlowInstance.fitView({ padding: 0.15, duration: 400 });
  }, [reactFlowInstance]);

  return (
    <div
      ref={containerRef}
      className={`h-screen flex flex-col bg-white ${presenting ? "presenting-mode" : ""}`}
      data-testid="diagram-editor"
    >
      {/* Header - hidden in présentation */}
      {!presenting && (
        <header className="h-12 md:h-14 bg-white/80 backdrop-blur-xl border-b border-gray-200 flex items-center justify-between px-3 md:px-5 z-50 shrink-0">
          <div className="flex items-center gap-2 md:gap-3 min-w-0 flex-1">
            <button onClick={() => navigate("/dashboard")} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-500 shrink-0" data-testid="back-btn">
              <ArrowLeft size={16} />
            </button>
            <input
              value={diagramTitle}
              onChange={(e) => setDiagramTitle(e.target.value)}
              className="font-semibold text-sm md:text-base bg-transparent border-none focus:outline-none text-gray-900 min-w-0 flex-1 placeholder:text-gray-300"
              placeholder="Sans titre..."
              data-testid="diagram-title-input"
            />
          </div>

          {/* Desktop actions */}
          <div className="hidden lg:flex items-center gap-1.5">
            <button onClick={undo} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400" title="Annuler" data-testid="undo-btn"><Undo2 size={15} /></button>
            <button onClick={redo} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400" title="Retablir" data-testid="redo-btn"><Redo2 size={15} /></button>
            <div className="w-px h-5 bg-gray-200 mx-1" />
            <button onClick={exportPng} className="bg-white text-gray-700 px-2.5 py-1.5 rounded-lg text-xs font-semibold border border-gray-200 flex items-center gap-1 hover:bg-gray-50 shadow-sm" data-testid="export-btn"><Download size={13} /> PNG</button>
            <button onClick={() => { if (requireEnterprise("Export PDF")) exportPdf(); }} className="bg-white text-gray-700 px-2.5 py-1.5 rounded-lg text-xs font-semibold border border-gray-200 flex items-center gap-1 hover:bg-gray-50 shadow-sm" data-testid="export-pdf-btn"><Download size={13} /> PDF {userPlan !== "enterprise" && user?.role !== "admin" && <Crown size={9} className="text-amber-400" />}</button>
            <button onClick={autoLayout} className="bg-white text-gray-700 px-2.5 py-1.5 rounded-lg text-xs font-semibold border border-gray-200 flex items-center gap-1 hover:bg-gray-50 shadow-sm" data-testid="auto-layout-btn"><LayoutGrid size={13} /> Layout</button>
            <button onClick={exportJson} className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 text-gray-500 shadow-sm" title="Export JSON" data-testid="export-json-btn"><FileJson size={14} /></button>
            <button onClick={() => importRef.current?.click()} className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 text-gray-500 shadow-sm" title="Import JSON" data-testid="import-json-btn"><Upload size={14} /></button>
            <button onClick={() => { setTemplateName(diagramTitle); setSaveTemplateOpen(true); }} className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 text-gray-500 shadow-sm" title="Modele" data-testid="save-template-btn"><BookmarkPlus size={14} /></button>
            <button onClick={() => setSearchOpen(v => !v)} className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 text-gray-500 shadow-sm" title="Rechercher" data-testid="search-btn"><Search size={14} /></button>
            <button onClick={() => setShortcutsOpen(true)} className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 text-gray-500 shadow-sm" title="Raccourcis" data-testid="shortcuts-btn"><Keyboard size={14} /></button>
            <button onClick={() => setSummaryOpen(v => !v)} className="bg-amber-50 text-amber-700 px-2.5 py-1.5 rounded-lg text-xs font-semibold border border-amber-200 flex items-center gap-1 hover:bg-amber-100 shadow-sm" title="Cahier des charges" data-testid="summary-btn"><FileText size={13} /> Specs</button>
            <button onClick={() => { if (requireEnterprise("Assistant IA")) setAssistantOpen(v => !v); }} className="bg-violet-50 text-violet-700 px-2.5 py-1.5 rounded-lg text-xs font-semibold border border-violet-200 flex items-center gap-1 hover:bg-violet-100 shadow-sm" title="Assistant IA vocal" data-testid="assistant-btn"><Mic size={13} /> Assistant {userPlan !== "enterprise" && user?.role !== "admin" && <Crown size={9} className="text-amber-400" />}</button>
            <input ref={importRef} type="file" accept=".json" onChange={handleImportJson} className="hidden" />
            <div className="w-px h-5 bg-gray-200 mx-1" />
            <button onClick={enterPresentation} className="bg-gray-900 text-white px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1 hover:bg-gray-800 shadow-sm" data-testid="presentation-btn"><Presentation size={13} /> Présenter</button>
            <button onClick={saveDiagram} disabled={saving} className="bg-indigo-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1 hover:bg-indigo-700 shadow-sm disabled:opacity-50" data-testid="save-btn"><Save size={13} /> {saving ? "..." : "Sauver"}</button>
            <button onClick={() => setVersionHistoryOpen(true)} className="bg-white text-gray-600 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold border border-gray-200 flex items-center gap-1 hover:bg-gray-50 shadow-sm" data-testid="version-history-btn"><History size={12} /> Versions</button>
            {isOwner && <button onClick={() => setShareOpen(true)} className="bg-white text-gray-700 px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-200 flex items-center gap-1 hover:bg-gray-50 shadow-sm" data-testid="share-btn"><Share2 size={13} /> Partager</button>}
            {isOwner && <button onClick={() => { if (requireEnterprise("Review client")) createReviewLink(); }} className="bg-emerald-50 text-emerald-700 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold border border-emerald-200 flex items-center gap-1 hover:bg-emerald-100 shadow-sm" data-testid="review-link-btn"><Eye size={12} /> Review {userPlan !== "enterprise" && user?.role !== "admin" && <Crown size={9} className="text-amber-400" />}</button>}
            {isOwner && <button onClick={() => setAccessesOpen(v => !v)} className="bg-violet-50 text-violet-700 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold border border-violet-200 flex items-center gap-1 hover:bg-violet-100 shadow-sm" data-testid="accesses-btn"><Key size={12} /> Acces</button>}
            <CollaborationBar
              diagramId={diagramId}
              connected={collab.connected}
              presence={collab.presence}
              myId={collab.myId}
              isEnterprise={isEnterprise}
              isOwner={isOwner}
              onStartCollab={() => requireEnterprise("Collaboration temps reel")}
            />
            <NotificationBell />
          </div>

          {/* Mobile/tablet actions */}
          <div className="flex lg:hidden items-center gap-1">
            <button onClick={undo} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400" data-testid="undo-btn-mobile"><Undo2 size={15} /></button>
            <button onClick={redo} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400" data-testid="redo-btn-mobile"><Redo2 size={15} /></button>
            <button onClick={saveDiagram} disabled={saving} className="bg-indigo-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1 hover:bg-indigo-700 shadow-sm disabled:opacity-50" data-testid="save-btn-mobile"><Save size={13} /></button>
            <div className="relative">
              <button onClick={() => setMobileMoreOpen(v => !v)} className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 text-gray-500" data-testid="more-btn-mobile"><MoreHorizontal size={16} /></button>
              {mobileMoreOpen && (
                <div className="absolute right-0 top-10 bg-white border border-gray-200 rounded-xl shadow-xl py-1 w-52 z-[100]" data-testid="mobile-more-menu">
                  <button onClick={() => { autoLayout(); setMobileMoreOpen(false); }} className="w-full px-4 py-2.5 text-left text-sm flex items-center gap-2.5 hover:bg-gray-50"><LayoutGrid size={14} className="text-gray-400" /> Auto-layout</button>
                  <button onClick={() => { exportPng(); setMobileMoreOpen(false); }} className="w-full px-4 py-2.5 text-left text-sm flex items-center gap-2.5 hover:bg-gray-50"><Download size={14} className="text-gray-400" /> Export PNG</button>
                  <button onClick={() => { if (requireEnterprise("Export PDF")) { exportPdf(); setMobileMoreOpen(false); } }} className="w-full px-4 py-2.5 text-left text-sm flex items-center gap-2.5 hover:bg-gray-50"><Download size={14} className="text-gray-400" /> Export PDF {userPlan !== "enterprise" && user?.role !== "admin" && <Crown size={9} className="text-amber-400 ml-auto" />}</button>
                  <button onClick={() => { exportJson(); setMobileMoreOpen(false); }} className="w-full px-4 py-2.5 text-left text-sm flex items-center gap-2.5 hover:bg-gray-50"><FileJson size={14} className="text-gray-400" /> Export JSON</button>
                  <button onClick={() => { setTemplateName(diagramTitle); setSaveTemplateOpen(true); setMobileMoreOpen(false); }} className="w-full px-4 py-2.5 text-left text-sm flex items-center gap-2.5 hover:bg-gray-50"><BookmarkPlus size={14} className="text-gray-400" /> Sauver modele</button>
                  <button onClick={() => { enterPresentation(); setMobileMoreOpen(false); }} className="w-full px-4 py-2.5 text-left text-sm flex items-center gap-2.5 hover:bg-gray-50"><Presentation size={14} className="text-gray-400" /> Présenter</button>
                  {isOwner && <button onClick={() => { setShareOpen(true); setMobileMoreOpen(false); }} className="w-full px-4 py-2.5 text-left text-sm flex items-center gap-2.5 hover:bg-gray-50"><Share2 size={14} className="text-gray-400" /> Partager</button>}
                  <button onClick={() => { setSearchOpen(v => !v); setMobileMoreOpen(false); }} className="w-full px-4 py-2.5 text-left text-sm flex items-center gap-2.5 hover:bg-gray-50"><Search size={14} className="text-gray-400" /> Rechercher</button>
                  <button onClick={() => { setSummaryOpen(v => !v); setMobileMoreOpen(false); }} className="w-full px-4 py-2.5 text-left text-sm flex items-center gap-2.5 hover:bg-gray-50"><FileText size={14} className="text-amber-500" /> Cahier des charges</button>
                  <input ref={importRef} type="file" accept=".json" onChange={handleImportJson} className="hidden" />
                </div>
              )}
            </div>
          </div>
        </header>
      )}

      {/* Page Tabs */}
      {!presenting && (pages.length > 0 || crossLinks.length > 0) && (
        <div className="h-9 bg-gray-50 border-b border-gray-200 flex items-center gap-0.5 px-3 overflow-x-auto shrink-0" data-testid="page-tabs">
          {/* Architecture view */}
          <button
            onClick={showArchitecture}
            className={`px-3 py-1.5 rounded-md text-[11px] font-semibold transition-all whitespace-nowrap flex items-center gap-1 ${
              showArchView ? "bg-indigo-600 text-white shadow-sm" : "text-indigo-500 hover:text-indigo-700 hover:bg-indigo-50 border border-indigo-200"
            }`}
            data-testid="arch-view-tab"
            title="Vue architecture inter-pages"
          >
            <LayoutGrid size={11} /> Architecture
          </button>
          <div className="w-px h-5 bg-gray-200 mx-1" />
          <button
            onClick={() => { setShowArchView(false); switchToPage(null); }}
            className={`px-3 py-1.5 rounded-md text-[11px] font-semibold transition-all whitespace-nowrap ${
              !activePageId && !showArchView ? "bg-white text-gray-900 shadow-sm border border-gray-200" : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
            }`}
            data-testid="page-tab-main"
          >
            Vue principale
          </button>
          {pages.map((page) => (
            <div key={page.id} className="flex items-center gap-0.5 group">
              <button
                onClick={() => { setShowArchView(false); switchToPage(page.id); }}
                className={`px-3 py-1.5 rounded-md text-[11px] font-semibold transition-all whitespace-nowrap ${
                  activePageId === page.id && !showArchView ? "bg-white text-gray-900 shadow-sm border border-gray-200" : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                }`}
                data-testid={`page-tab-${page.id}`}
              >
                {page.title}
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); removePage(page.id); }}
                className="w-4 h-4 rounded text-gray-300 hover:text-rose-500 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-all"
              >
                <X size={10} />
              </button>
            </div>
          ))}
          <button
            onClick={() => {
              const name = prompt("Nom de la page :");
              if (name) addPage(name);
            }}
            className="px-2 py-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
            data-testid="add-page-btn"
          >
            <FilePlus size={14} />
          </button>
          {/* Add cross-link button (only in architecture view) */}
          {showArchView && pages.length >= 1 && (
            <>
              <div className="w-px h-5 bg-gray-200 mx-1" />
              <button
                onClick={() => {
                  const allPages = [{ id: "main", title: "Vue principale" }, ...pages];
                  const srcName = prompt(`Lien inter-pages : Source ?\n${allPages.map((p, i) => `${i + 1}. ${p.title}`).join("\n")}`);
                  if (!srcName) return;
                  const srcIdx = parseInt(srcName) - 1;
                  if (isNaN(srcIdx) || srcIdx < 0 || srcIdx >= allPages.length) return;
                  const tgtName = prompt(`Cible ?\n${allPages.map((p, i) => `${i + 1}. ${p.title}`).join("\n")}`);
                  if (!tgtName) return;
                  const tgtIdx = parseInt(tgtName) - 1;
                  if (isNaN(tgtIdx) || tgtIdx < 0 || tgtIdx >= allPages.length || tgtIdx === srcIdx) return;
                  const label = prompt("Label du lien (optionnel) :") || "";
                  addCrossLink(allPages[srcIdx].id, allPages[tgtIdx].id, label);
                  showArchitecture(); // Refresh
                }}
                className="px-2.5 py-1.5 text-rose-500 hover:text-rose-700 hover:bg-rose-50 rounded-md transition-colors text-[11px] font-semibold flex items-center gap-1 border border-rose-200"
                data-testid="add-cross-link-btn"
              >
                <Link size={11} /> Lien inter-pages
              </button>
            </>
          )}
        </div>
      )}

      {/* Main */}
      <div className="flex flex-1 overflow-hidden">
        {!presenting && <div className="hidden md:block"><Toolbox /></div>}

        <div
          className="flex-1 relative"
          ref={reactFlowWrapper}
          onMouseMove={(e) => {
            if (!collab.connected || !reactFlowInstance) return;
            const bounds = e.currentTarget.getBoundingClientRect();
            const viewport = reactFlowInstance.getViewport();
            const x = (e.clientX - bounds.left - viewport.x) / viewport.zoom;
            const y = (e.clientY - bounds.top - viewport.y) / viewport.zoom;
            collab.sendCursorMove({ x, y });
          }}
        >
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={presenting ? undefined : onNodesChange}
            onEdgesChange={presenting ? undefined : onEdgesChange}
            onConnect={presenting ? undefined : onConnect}
            onInit={setReactFlowInstance}
            onDragOver={presenting ? undefined : onDragOver}
            onDrop={presenting ? undefined : onDrop}
            onNodeClick={presenting ? undefined : onNodeClick}
            onPaneClick={presenting ? undefined : onPaneClick}
            onEdgeClick={presenting ? undefined : onEdgeClick}
            onEdgeDoubleClick={presenting ? undefined : onEdgeDoubleClick}
            onNodeDragStop={presenting ? undefined : (_, node) => {
              if (!collabRemoteRef.current && collab.connected) {
                collab.sendNodeMove(node.id, node.position);
              }
            }}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            defaultEdgeOptions={defaultEdgeOptions}
            fitView
            snapToGrid={!presenting}
            snapGrid={[16, 16]}
            nodesDraggable={!presenting}
            nodesConnectable={!presenting}
            elementsSelectable={!presenting}
            panOnDrag={true}
            zoomOnScroll={true}
          >
            <Background
              gap={presenting ? 0 : 24}
              size={presenting ? 0 : 1.5}
              color={presenting ? "transparent" : "#E5E7EB"}
              style={presenting ? { backgroundColor: "#FAFAFA" } : { backgroundColor: "#F9FAFB" }}
              variant="dots"
            />
            {!presenting && <Controls position="bottom-right" className="hidden md:flex" />}
            {!presenting && (
              <MiniMap
                position="bottom-left"
                nodeColor={(n) => n.data?.color || "#6366F1"}
                style={{ border: "1px solid #E5E7EB", borderRadius: 12 }}
                className="hidden md:block"
              />
            )}
          </ReactFlow>

          {/* Collaboration cursors */}
          {collab.connected && (
            <CollaborationCursors
              cursors={collab.cursors}
              viewport={reactFlowInstance?.getViewport()}
            />
          )}

          {/* Edge selected: floating delete */}
          {selectedEdgeId && !presenting && (
            <div className="absolute top-16 left-1/2 -translate-x-1/2 z-[45] bg-white border border-gray-200 rounded-xl shadow-xl px-3 py-2 flex items-center gap-2 animate-in fade-in" data-testid="edge-action-bar">
              <span className="text-[10px] text-gray-500 font-medium">Connecteur sélectionné</span>
              <button
                onClick={() => { setEditingEdgeId(selectedEdgeId); setSelectedEdgeId(null); }}
                className="text-[10px] font-semibold text-indigo-600 bg-indigo-50 px-2.5 py-1 rounded-lg hover:bg-indigo-100 transition-colors"
                data-testid="edge-edit-btn"
              >
                Modifier
              </button>
              <button
                onClick={() => deleteEdge(selectedEdgeId)}
                className="text-[10px] font-semibold text-rose-600 bg-rose-50 px-2.5 py-1 rounded-lg hover:bg-rose-100 transition-colors"
                data-testid="edge-delete-selected-btn"
              >
                Supprimer
              </button>
            </div>
          )}

          {/* Floating search bar */}
          {searchOpen && !presenting && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 bg-white/95 backdrop-blur-md border border-gray-200 rounded-xl shadow-xl px-4 py-2.5 w-80" data-testid="search-bar">
              <Search size={16} className="text-gray-400 shrink-0" />
              <input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Rechercher un élément..."
                className="flex-1 bg-transparent text-sm focus:outline-none placeholder:text-gray-300"
                autoFocus
                data-testid="search-input"
              />
              {searchTerm && (
                <span className="text-[10px] text-gray-400 shrink-0">
                  {nodes.filter((n) => {
                    const t = searchTerm.toLowerCase();
                    const d = n.data;
                    return d.title?.toLowerCase().includes(t) || d.description?.toLowerCase().includes(t) || d.tags?.some((tag) => tag.toLowerCase().includes(t));
                  }).length}/{nodes.length}
                </span>
              )}
              <button
                onClick={() => { setSearchOpen(false); setSearchTerm(""); }}
                className="w-5 h-5 rounded hover:bg-gray-100 flex items-center justify-center text-gray-400 shrink-0"
              >
                <X size={12} />
              </button>
            </div>
          )}

          {/* Onboarding Tour */}
          <OnboardingTour step={onboardingStep} setStep={setOnboardingStep} presenting={presenting} />

          {/* Présentation overlay with slideshow */}
          {presenting && (
            <PresentationOverlay
              diagramTitle={diagramTitle}
              presSlides={presSlides}
              presSlideIdx={presSlideIdx}
              prevSlide={prevSlide}
              nextSlide={nextSlide}
              goToSlide={goToSlide}
              presentationZoomIn={presentationZoomIn}
              presentationZoomOut={presentationZoomOut}
              exitPresentationWithCleanup={exitPresentationWithCleanup}
              ttsVoice={ttsVoice}
              setTtsVoice={setTtsVoice}
              ttsEnabled={ttsEnabled}
              setTtsEnabled={setTtsEnabled}
              ttsLoading={ttsLoading}
              isRecordingVideo={isRecordingVideo}
              startVideoRecording={startVideoRecording}
              stopVideoRecording={stopVideoRecording}
              requireEnterprise={requireEnterprise}
              userPlan={userPlan}
              user={user}
              autoPlaying={autoPlaying}
              onToggleAutoPlay={toggleAutoPlay}
              videoReady={videoReady}
              onDownloadVideo={downloadVideo}
              onConvertMp4={convertToMp4}
              mp4Converting={mp4Converting}
              mp4Ready={mp4Ready}
              mp4Url={mp4Url}
              recordingDuration={recordingDuration}
            />
          )}
        </div>

        {!presenting && selectedNode && (
          <div className="hidden md:block">
            <PropertiesPanel
              node={selectedNode}
              onUpdate={updateNodeData}
              onDelete={deleteNode}
              onDuplicate={duplicateNode}
              onClose={() => setSelectedNode(null)}
              onRecalculate={runCalculations}
            />
          </div>
        )}
        {/* Mobile properties dialog - only renders on mobile */}
        {!presenting && selectedNode && isMobile && (
          <Dialog open={!!selectedNode} onOpenChange={(open) => { if (!open) setSelectedNode(null); }}>
            <DialogContent className="rounded-xl border border-gray-200 shadow-xl bg-white max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-semibold text-sm">Propriétés</DialogTitle>
                <DialogDescription className="text-xs text-gray-400">Modifier cet élément</DialogDescription>
              </DialogHeader>
              <div className="flex flex-col gap-3 mt-1">
                <Input value={selectedNode.data.title || ""} onChange={(e) => updateNodeData(selectedNode.id, { title: e.target.value })} placeholder="Titre" className="rounded-lg border-gray-200 text-sm" />
                <Input value={selectedNode.data.amount || ""} onChange={(e) => updateNodeData(selectedNode.id, { amount: e.target.value })} placeholder="Montant" className="rounded-lg border-gray-200 text-sm font-mono" />
                <textarea value={selectedNode.data.description || ""} onChange={(e) => updateNodeData(selectedNode.id, { description: e.target.value })} placeholder="Description" className="min-h-[60px] rounded-lg border border-gray-200 p-2.5 text-sm focus:outline-none resize-none" />
                <div className="flex gap-2">
                  <button onClick={() => { duplicateNode(); setSelectedNode(null); }} className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg text-xs font-semibold">Dupliquer</button>
                  <button onClick={() => { deleteNode(selectedNode.id); }} className="flex-1 bg-rose-500 text-white py-2 rounded-lg text-xs font-semibold">Supprimér</button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Mobile Floating Toolbar */}
      {!presenting && (
        <div className="md:hidden fixed bottom-5 left-1/2 -translate-x-1/2 z-50 flex flex-col items-center gap-2" data-testid="mobile-toolbar">
          {/* Connect mode banner */}
          {mobileConnectMode && (
            <div className="bg-violet-600 text-white px-4 py-2 rounded-xl shadow-xl text-xs font-semibold flex items-center gap-2 animate-in fade-in slide-in-from-bottom-2" data-testid="connect-mode-banner">
              <Link size={14} />
              {connectSource ? "Touchez la carte cible" : "Touchez la carte source"}
              <button onClick={() => { setMobileConnectMode(false); setConnectSource(null); }} className="ml-1 w-5 h-5 rounded-full bg-white/20 flex items-center justify-center">
                <X size={10} />
              </button>
            </div>
          )}

          {/* Add node popup menu */}
          {mobileAddOpen && (
            <div className="bg-white border border-gray-200 rounded-xl shadow-xl py-2 w-48 animate-in fade-in slide-in-from-bottom-2 mb-1" data-testid="mobile-add-menu">
              {[
                { type: "company", label: "Entreprise", color: "#6366F1" },
                { type: "subsidiary", label: "Filiale", color: "#3B82F6" },
                { type: "person", label: "Personne", color: "#7C3AED" },
                { type: "logo", label: "Logo", color: "#6366F1" },
                { type: "service", label: "Service", color: "#0EA5E9" },
                { type: "job", label: "Metier", color: "#F59E0B" },
                { type: "scenario", label: "Scenario", color: "#F43F5E" },
                { type: "note", label: "Note", color: "#F59E0B" },
                { type: "metric", label: "KPI", color: "#10B981" },
                { type: "info", label: "Info", color: "#6366F1" },
                { type: "price", label: "Tarif", color: "#10B981" },
                { type: "label", label: "Etiquette", color: "#1F2937" },
                { type: "legend", label: "Legende", color: "#8B5CF6" },
                { type: "header", label: "Header", color: "#6366F1" },
                { type: "hero", label: "Hero", color: "#7C3AED" },
                { type: "cta", label: "CTA", color: "#F43F5E" },
                { type: "section", label: "Section", color: "#3B82F6" },
                { type: "footer", label: "Footer", color: "#6B7280" },
              ].map((item) => (
                <button
                  key={item.type}
                  onClick={() => addNodeAtCenter(item.type)}
                  className="w-full px-4 py-2 text-left text-sm flex items-center gap-2.5 hover:bg-gray-50 active:bg-gray-100"
                  data-testid={`mobile-add-${item.type}`}
                >
                  <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: item.color }} />
                  {item.label}
                </button>
              ))}
            </div>
          )}

          {/* Toolbar pill */}
          <div className="bg-white/95 backdrop-blur-lg border border-gray-200 rounded-2xl shadow-xl px-2 py-1.5 flex items-center gap-1" data-testid="mobile-toolbar-pill">
            {/* Zoom out */}
            <button
              onClick={() => reactFlowInstance?.zoomOut({ duration: 200 })}
              className="w-10 h-10 rounded-xl flex items-center justify-center text-gray-500 hover:bg-gray-100 active:bg-gray-200 transition-colors"
              data-testid="mobile-zoom-out"
            >
              <Minus size={16} />
            </button>

            {/* Zoom in */}
            <button
              onClick={() => reactFlowInstance?.zoomIn({ duration: 200 })}
              className="w-10 h-10 rounded-xl flex items-center justify-center text-gray-500 hover:bg-gray-100 active:bg-gray-200 transition-colors"
              data-testid="mobile-zoom-in"
            >
              <ZoomIn size={16} />
            </button>

            {/* Fit view */}
            <button
              onClick={() => reactFlowInstance?.fitView({ padding: 0.12, duration: 400 })}
              className="w-10 h-10 rounded-xl flex items-center justify-center text-gray-500 hover:bg-gray-100 active:bg-gray-200 transition-colors"
              data-testid="mobile-fit-view"
            >
              <Maximize2 size={16} />
            </button>

            {/* Divider */}
            <div className="w-px h-6 bg-gray-200 mx-0.5" />

            {/* Auto-layout */}
            <button
              onClick={autoLayout}
              className="w-10 h-10 rounded-xl flex items-center justify-center text-gray-500 hover:bg-gray-100 active:bg-gray-200 transition-colors"
              data-testid="mobile-auto-layout"
            >
              <LayoutGrid size={16} />
            </button>

            {/* Connect mode */}
            <button
              onClick={() => {
                setMobileConnectMode((v) => !v);
                setConnectSource(null);
                setMobileAddOpen(false);
                if (!mobileConnectMode) toast.info("Mode connexion : touchez 2 cartes");
              }}
              className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${
                mobileConnectMode
                  ? "bg-violet-600 text-white shadow-md"
                  : "text-gray-500 hover:bg-gray-100 active:bg-gray-200"
              }`}
              data-testid="mobile-connect-btn"
            >
              <Link size={16} />
            </button>

            {/* Add node */}
            <button
              onClick={() => {
                setMobileAddOpen((v) => !v);
                setMobileConnectMode(false);
                setConnectSource(null);
              }}
              className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all shadow-sm ${
                mobileAddOpen
                  ? "bg-gray-900 text-white rotate-45"
                  : "bg-indigo-600 text-white"
              }`}
              data-testid="mobile-add-fab"
            >
              <Plus size={22} />
            </button>
          </div>
        </div>
      )}

      {/* Share Dialog */}
      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="rounded-xl border border-gray-200 shadow-xl bg-white">
          <DialogHeader>
            <DialogTitle className="font-semibold text-lg">
              Partager le diagramme
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-500">
              Invitez un utilisateur ou creez un lien public.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-5 mt-2">
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                Partager avec un utilisateur
              </p>
              <div className="flex gap-2">
                <Input
                  value={shareEmail}
                  onChange={(e) => setShareEmail(e.target.value)}
                  placeholder="email@exemple.com"
                  className="rounded-lg border-gray-200 flex-1"
                  data-testid="share-email-input"
                />
                <button
                  onClick={shareWithUser}
                  className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-xs font-semibold hover:bg-indigo-700 transition-colors whitespace-nowrap shadow-sm"
                  data-testid="share-submit-btn"
                >
                  Partager
                </button>
              </div>
            </div>
            <div className="border-t border-gray-100 pt-4">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                Ou créer un lien public
              </p>
              <button
                onClick={createShareLink}
                className="bg-white text-gray-700 px-4 py-2 rounded-lg text-xs font-semibold border border-gray-200 hover:bg-gray-50 transition-colors w-full shadow-sm"
                data-testid="create-share-link-btn"
              >
                Générer un lien
              </button>
              {shareLink && (
                <div className="mt-2 p-3 bg-gray-50 border border-gray-200 rounded-lg font-mono text-xs break-all text-indigo-600">
                  {shareLink}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edge Label & Color Edit Dialog */}
      <Dialog open={!!editingEdgeId} onOpenChange={(open) => { if (!open) { setEditingEdgeId(null); setEdgeLabel(""); setEdgeColor("indigo"); setEdgeDirection("forward"); } }}>
        <DialogContent className="rounded-xl border border-gray-200 shadow-xl bg-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-semibold text-base">
              Connecteur
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-500">
              Personnalisez le label et la couleur du flux.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4 mt-2">
            <div>
              <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5 block">Label</label>
              <Input
                value={edgeLabel}
                onChange={(e) => setEdgeLabel(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && saveEdgeLabel()}
                placeholder="Ex: Filiale a 80%"
                className="rounded-lg border-gray-200"
                autoFocus
                data-testid="edge-label-input"
              />
            </div>
            <div>
              <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5 block">Couleur du flux</label>
              <div className="flex gap-2 flex-wrap">
                {Object.entries(EDGE_COLORS).map(([key, palette]) => (
                  <button
                    key={key}
                    onClick={() => setEdgeColor(key)}
                    className={`w-8 h-8 rounded-full transition-all flex items-center justify-center ${
                      edgeColor === key ? "ring-2 ring-offset-2 ring-gray-400 scale-110" : "hover:scale-110"
                    }`}
                    style={{ backgroundColor: palette.accent }}
                    title={key}
                    data-testid={`edge-color-${key}`}
                  >
                    {edgeColor === key && (
                      <svg viewBox="0 0 12 12" className="w-3 h-3 text-white" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M2 6l3 3 5-5"/></svg>
                    )}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5 block">Sens de la fleche</label>
              <div className="flex gap-1.5">
                {[
                  { value: "forward", label: "A → B", icon: "→" },
                  { value: "backward", label: "A ← B", icon: "←" },
                  { value: "both", label: "A ↔ B", icon: "↔" },
                  { value: "none", label: "A — B", icon: "—" },
                ].map((d) => (
                  <button
                    key={d.value}
                    onClick={() => setEdgeDirection(d.value)}
                    className={`flex-1 py-2 rounded-lg text-xs font-semibold border transition-colors text-center ${
                      edgeDirection === d.value
                        ? "bg-gray-900 text-white border-gray-900"
                        : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50"
                    }`}
                    data-testid={`edge-dir-${d.value}`}
                  >
                    <span className="text-sm block">{d.icon}</span>
                    <span className="text-[9px] opacity-70">{d.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={saveEdgeLabel}
                className="flex-1 bg-indigo-600 text-white px-4 py-2 rounded-lg text-xs font-semibold hover:bg-indigo-700 transition-colors"
                data-testid="edge-label-save-btn"
              >
                Appliquer
              </button>
              <button
                onClick={() => { setEdgeLabel(""); saveEdgeLabel(); }}
                className="px-4 py-2 rounded-lg text-xs font-semibold text-gray-500 border border-gray-200 hover:bg-gray-50 transition-colors"
                data-testid="edge-label-clear-btn"
              >
                Supprimer label
              </button>
              <button
                onClick={() => { deleteEdge(editingEdgeId); setEditingEdgeId(null); setEdgeLabel(""); setEdgeColor("indigo"); setEdgeDirection("forward"); }}
                className="px-4 py-2 rounded-lg text-xs font-semibold text-rose-600 border border-rose-200 hover:bg-rose-50 transition-colors"
                data-testid="edge-delete-btn"
              >
                Supprimer le connecteur
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Save as Template Dialog */}
      <Dialog open={saveTemplateOpen} onOpenChange={setSaveTemplateOpen}>
        <DialogContent className="rounded-xl border border-gray-200 shadow-xl bg-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-semibold text-base">
              Sauvegarder comme modele
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-500">
              Ce diagramme sera disponible comme template reutilisable.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 mt-2">
            <Input
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder="Nom du modele"
              className="rounded-lg border-gray-200"
              data-testid="template-name-input"
            />
            <Input
              value={templateDesc}
              onChange={(e) => setTemplateDesc(e.target.value)}
              placeholder="Description courte"
              className="rounded-lg border-gray-200"
              data-testid="template-desc-input"
            />
            <div className="flex flex-wrap gap-1.5">
              {["custom", "entreprise", "tech", "workflow", "process", "stratégie", "marketing", "media"].map((cat) => (
                <button
                  key={cat}
                  onClick={() => setTemplateCategory(cat)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                    templateCategory === cat
                      ? "bg-indigo-100 text-indigo-700 ring-2 ring-indigo-500/20"
                      : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                  }`}
                >
                  {cat === "custom" ? "Mes modèles" : cat === "entreprise" ? "Entreprise" : cat === "tech" ? "Tech & SaaS" : cat === "workflow" ? "Workflow" : cat === "process" ? "Production" : cat === "stratégie" ? "Stratégie" : cat === "marketing" ? "Marketing" : "Creative"}
                </button>
              ))}
            </div>
            <button
              onClick={saveAsTemplate}
              className="bg-indigo-600 text-white px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-indigo-700 transition-colors shadow-sm flex items-center justify-center gap-2"
              data-testid="confirm-save-template-btn"
            >
              <BookmarkPlus size={14} /> Sauvegarder le modele
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Shortcuts Dialog */}
      <Dialog open={shortcutsOpen} onOpenChange={setShortcutsOpen}>
        <DialogContent className="rounded-xl border border-gray-200 shadow-xl bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-semibold text-base">
              Raccourcis clavier
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-500">
              Toutes les actions rapides disponibles
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-y-2.5 gap-x-6 mt-3 text-sm">
            {[
              ["Ctrl + S", "Sauvegarder"],
              ["Ctrl + Z", "Annuler"],
              ["Ctrl + Shift + Z", "Retablir"],
              ["Ctrl + D", "Dupliquer"],
              ["Ctrl + F", "Rechercher"],
              ["Suppr / Backspace", "Supprimér élément"],
              ["Double-clic connectéur", "Editer le label"],
              ["Echap", "Quitter présentation / recherche"],
              ["?", "Afficher les raccourcis"],
            ].map(([key, action]) => (
              <div key={key} className="contents">
                <kbd className="bg-gray-100 border border-gray-200 rounded-md px-2 py-1 text-[11px] font-mono text-gray-600 text-center">
                  {key}
                </kbd>
                <span className="text-gray-600 text-xs self-center">{action}</span>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Summary / Specs / Planning Panel */}
      {summaryOpen && (
        <div className="fixed inset-0 z-[60] flex justify-end" data-testid="summary-panel">
          <div className="absolute inset-0 bg-black/20 backdrop-blur-sm" onClick={() => setSummaryOpen(false)} />
          <div className="relative w-full max-w-xl bg-white border-l border-gray-200 shadow-2xl h-full flex flex-col overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 shrink-0">
              <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
                <button
                  onClick={() => setSummaryTab("specs")}
                  className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${summaryTab === "specs" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500"}`}
                  data-testid="tab-specs"
                >
                  Cahier des charges
                </button>
                <button
                  onClick={() => setSummaryTab("planning")}
                  className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${summaryTab === "planning" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500"}`}
                  data-testid="tab-planning"
                >
                  Planning
                </button>
              </div>
              <div className="flex items-center gap-2">
                {summaryTab === "specs" && (
                  <>
                    <button
                      onClick={() => {
                        const text = generateSummary(diagramTitle, diagramType, nodes, edges);
                        navigator.clipboard.writeText(text);
                        toast.success("Copie");
                      }}
                      className="bg-gray-100 text-gray-700 px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-gray-200"
                      data-testid="copy-summary-btn"
                    >
                      Copier
                    </button>
                    <button
                      onClick={() => {
                        const text = generateSummary(diagramTitle, diagramType, nodes, edges);
                        const blob = new Blob([text], { type: "text/markdown" });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.download = `${diagramTitle || "specs"}.md`;
                        a.href = url;
                        a.click();
                        URL.revokeObjectURL(url);
                      }}
                      className="bg-indigo-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-indigo-700"
                      data-testid="export-summary-btn"
                    >
                      .md
                    </button>
                    <button
                      onClick={async () => {
                        const text = generateSummary(diagramTitle, diagramType, nodes, edges);
                        await exportSpecsPdfWithDiagram(diagramTitle, text);
                        toast.success("PDF exporte avec schéma en annexe !");
                      }}
                      className="bg-rose-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-rose-700"
                      data-testid="export-specs-pdf-btn"
                    >
                      PDF
                    </button>
                  </>
                )}
                {summaryTab === "planning" && planningRows.length === 0 && (
                  <button
                    onClick={async () => {
                      setAiLoading(true);
                      try {
                        const specs = generateSummary(diagramTitle, diagramType, nodes, edges);
                        const { data } = await axios.post(`${API}/ai/planning`, { specs_text: specs }, { withCredentials: true });
                        const parsed = parseAiPlanning(data.planning);
                        setPlanningRows(parsed);
                        toast.success("Planning genere !");
                      } catch (e) {
                        toast.error(e.response?.data?.detail || "Erreur IA");
                      }
                      setAiLoading(false);
                    }}
                    disabled={aiLoading}
                    className="bg-violet-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-violet-700 flex items-center gap-1.5 disabled:opacity-50 shadow-sm"
                    data-testid="generate-planning-btn"
                  >
                    {aiLoading ? <Loader2 size={13} className="animate-spin" /> : <Zap size={13} />}
                    {aiLoading ? "Generation..." : "Générer avec l'IA"}
                  </button>
                )}
                <button onClick={() => setSummaryOpen(false)} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400">
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-5 py-4">
              {summaryTab === "specs" && (
                <div className="prose prose-sm max-w-none text-gray-700 leading-relaxed">
                  {generateSummary(diagramTitle, diagramType, nodes, edges).split("\n").map((line, i) => {
                    if (line.startsWith("# ")) return <h1 key={i} className="text-xl font-bold text-gray-900 mt-0 mb-3">{line.slice(2)}</h1>;
                    if (line.startsWith("## ")) return <h2 key={i} className="text-base font-bold text-gray-900 mt-6 mb-2 pb-1 border-b border-gray-200">{line.slice(3)}</h2>;
                    if (line.startsWith("### ")) return <h3 key={i} className="text-sm font-semibold text-gray-700 mt-4 mb-1">{line.slice(4)}</h3>;
                    if (line.startsWith("#### ")) return <h4 key={i} className="text-sm font-semibold text-gray-600 mt-3 mb-0.5">{line.slice(5)}</h4>;
                    if (line.startsWith("> ")) return <blockquote key={i} className="text-xs text-gray-400 italic border-l-2 border-gray-200 pl-3 my-1">{line.slice(2)}</blockquote>;
                    if (line.startsWith("| ") && line.includes("|")) {
                      const cells = line.split("|").filter(Boolean).map(c => c.trim());
                      if (cells.every(c => c.match(/^-+$/))) return null;
                      return <div key={i} className="flex gap-2 text-xs py-1 border-b border-gray-100">{cells.map((cell, j) => <span key={j} className={`flex-1 ${j === 0 ? "font-medium text-gray-700" : "text-gray-500"}`}>{cell}</span>)}</div>;
                    }
                    if (line.startsWith("- **")) {
                      const match = line.match(/^- \*\*(.+?)\*\*\s*:?\s*(.*)/);
                      if (match) return <div key={i} className="flex gap-1 text-xs my-0.5"><span className="font-semibold text-gray-700 shrink-0">{match[1]} :</span><span className="text-gray-500">{match[2]}</span></div>;
                    }
                    if (line.startsWith("- ")) return <div key={i} className="text-xs text-gray-600 pl-3 my-0.5">{line.slice(2)}</div>;
                    if (line.startsWith("  - ")) return <div key={i} className="text-xs text-gray-500 pl-6 my-0.5">{line.slice(4)}</div>;
                    if (line.startsWith("---")) return <hr key={i} className="my-4 border-gray-200" />;
                    if (line.startsWith("*") && line.endsWith("*")) return <p key={i} className="text-[10px] text-gray-400 italic mt-4">{line.replace(/\*/g, "")}</p>;
                    if (line.includes("`")) {
                      const parts = line.split(/(`[^`]+`)/);
                      return <p key={i} className="text-xs my-0.5">{parts.map((p, j) => p.startsWith("`") ? <code key={j} className="bg-gray-100 px-1 py-0.5 rounded text-indigo-600 text-[10px]">{p.slice(1, -1)}</code> : <span key={j}>{p}</span>)}</p>;
                    }
                    if (line.trim() === "") return <div key={i} className="h-2" />;
                    return <p key={i} className="text-xs text-gray-600 my-0.5">{line}</p>;
                  })}
                </div>
              )}

              {summaryTab === "planning" && (
                <div>
                  {planningRows.length > 0 ? (
                    <PlanningTable initialRows={planningRows} />
                  ) : (
                    <div className="text-center py-16">
                      <Zap size={40} className="mx-auto mb-3 text-violet-300" />
                      <p className="text-sm text-gray-500 mb-1">Aucun planning genere</p>
                      <p className="text-xs text-gray-400 mb-4 max-w-xs mx-auto">
                        L'IA va analyser votre schéma et generer un tableau de planning detaille avec estimations, responsables et budget.
                      </p>
                      <button
                        onClick={async () => {
                          setAiLoading(true);
                          try {
                            const specs = generateSummary(diagramTitle, diagramType, nodes, edges);
                            const { data } = await axios.post(`${API}/ai/planning`, { specs_text: specs }, { withCredentials: true });
                            const parsed = parseAiPlanning(data.planning);
                            setPlanningRows(parsed);
                            toast.success("Planning genere !");
                          } catch (e) {
                            toast.error(e.response?.data?.detail || "Erreur IA");
                          }
                          setAiLoading(false);
                        }}
                        disabled={aiLoading}
                        className="bg-violet-600 text-white px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-violet-700 inline-flex items-center gap-2 disabled:opacity-50 shadow-sm"
                        data-testid="generate-planning-btn-main"
                      >
                        {aiLoading ? <Loader2 size={16} className="animate-spin" /> : <Zap size={16} />}
                        {aiLoading ? "Analyse en cours..." : "Générer le planning IA"}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Enterprise Upgrade Prompt */}
      <EnterpriseGateModal open={upgradePromptOpen} onOpenChange={setUpgradePromptOpen} featureName={upgradeFeatureName} />

      {/* Version History Panel */}
      <VersionHistoryPanel
        diagramId={diagramId}
        open={versionHistoryOpen}
        onClose={() => setVersionHistoryOpen(false)}
        onRestore={() => { setVersionHistoryOpen(false); window.location.reload(); }}
      />

      {/* Accèsses Panel */}
      {accessesOpen && (
        <div className="fixed inset-0 z-[60] flex justify-end" data-testid="accesses-panel">
          <div className="absolute inset-0 bg-black/20 backdrop-blur-sm" onClick={() => setAccessesOpen(false)} />
          <div className="relative w-full max-w-md bg-white border-l border-gray-200 shadow-2xl h-full flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 shrink-0">
              <div className="flex items-center gap-2">
                <Key size={16} className="text-violet-600" />
                <h3 className="font-semibold text-sm text-gray-900">Accès & Clés API</h3>
              </div>
              <button onClick={() => setAccessesOpen(false)} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400"><X size={16} /></button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
              <p className="text-[10px] text-gray-400 leading-relaxed">
                Definissez les accès requis pour ce projet. Le client pourra les fournir via le portail client. Les valeurs sont masquees par securite (visibles 30s au clic).
              </p>

              {accesses.map((acc) => (
                <div key={acc.id} className={`rounded-xl border p-3 ${acc.status === "provided" ? "border-emerald-200 bg-emerald-50/50" : "border-amber-200 bg-amber-50/50"}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-semibold text-xs text-gray-900">{acc.label}</span>
                    <div className="flex items-center gap-1.5">
                      <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">{acc.access_type}</span>
                      <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${acc.assigned_to === "client" ? "bg-blue-100 text-blue-700" : "bg-violet-100 text-violet-700"}`}>
                        {acc.assigned_to === "client" ? "Client" : "Prestataire"}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-[10px]">
                    {acc.has_value || acc.status === "provided" ? (
                      <span className="text-emerald-600 flex items-center gap-1"><CheckCircle size={10} /> Fourni — ********</span>
                    ) : (
                      <span className="text-amber-600 flex items-center gap-1"><XCircle size={10} /> En attente</span>
                    )}
                  </div>
                </div>
              ))}

              {/* Add new access */}
              <div className="border-t border-gray-100 pt-3">
                <h4 className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Ajouter un accès requis</h4>
                <div className="space-y-2">
                  <input
                    value={newAccessLabel}
                    onChange={e => setNewAccessLabel(e.target.value)}
                    placeholder="Ex: Cle API Stripe, Accès FTP..."
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-xs focus:outline-none focus:border-violet-400"
                    data-testid="new-access-label"
                  />
                  <div className="flex gap-2">
                    <select value={newAccessType} onChange={e => setNewAccessType(e.target.value)} className="flex-1 rounded-lg border border-gray-200 px-2 py-2 text-xs focus:outline-none">
                      <option value="api_key">Cle API</option>
                      <option value="credentials">Identifiants</option>
                      <option value="url">URL</option>
                      <option value="other">Autre</option>
                    </select>
                    <select value={newAccessFor} onChange={e => setNewAccessFor(e.target.value)} className="flex-1 rounded-lg border border-gray-200 px-2 py-2 text-xs focus:outline-none">
                      <option value="client">A fournir: Client</option>
                      <option value="provider">A fournir: Prestataire</option>
                    </select>
                  </div>
                  <button
                    onClick={async () => {
                      if (!newAccessLabel.trim()) return;
                      try {
                        await axios.post(`${API}/diagrams/${diagramId}/accesses`, {
                          diagram_id: diagramId,
                          label: newAccessLabel,
                          access_type: newAccessType,
                          assigned_to: newAccessFor,
                        }, { withCredentials: true });
                        setNewAccessLabel("");
                        const { data: accs } = await axios.get(`${API}/diagrams/${diagramId}/accesses`, { withCredentials: true });
                        setAccesses(accs || []);
                      } catch { /* ignore */ }
                    }}
                    disabled={!newAccessLabel.trim()}
                    className="w-full bg-violet-600 text-white py-2 rounded-lg text-xs font-semibold hover:bg-violet-700 disabled:opacity-30 flex items-center justify-center gap-1"
                    data-testid="add-access-btn"
                  >
                    <Plus size={12} /> Ajouter
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AI Assistant Chat Panel */}
      {assistantOpen && (
        <AIChatPanel
          onClose={() => setAssistantOpen(false)}
          chatMessages={chatMessages}
          setChatMessages={setChatMessages}
          chatInput={chatInput}
          setChatInput={setChatInput}
          sendChatMessage={sendChatMessage}
          generateFromChat={generateFromChat}
          assistantLoading={assistantLoading}
          isRecording={isRecording}
          startRecording={startRecording}
          stopRecording={stopRecording}
          chatEndRef={chatEndRef}
          pages={pages}
        />
      )}
    </div>
  );
}

export default function DiagramEditor() {
  return (
    <ReactFlowProvider>
      <DiagramEditorInner />
    </ReactFlowProvider>
  );
}

